import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  cn,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Check, CheckCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  dismiss,
  fetchInbox,
  fetchUnreadCount,
  markAllRead,
  markRead,
  type InboxNotification,
} from '../../../api/notifications.js';

const POLL_MS = 30_000;
const CATEGORY_LABELS: Record<string, string> = {
  work_order: 'Work orders',
  violation: 'Violations',
  compliance: 'Compliance',
  inspection: 'Inspections',
  vendor: 'Vendor',
  reminder: 'Reminders',
  system: 'System',
};

/**
 * Phase 9 — the persistent bell next to the avatar. Polls the
 * unread-count endpoint every 30s and on window focus. Opening it
 * renders the inbox SheetContent, grouped by Today / This week /
 * Earlier with read/dismiss controls on each row.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const countQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const unread = countQuery.data?.count ?? 0;

  const inboxQuery = useQuery({
    queryKey: ['notifications', 'inbox', 'drawer'],
    queryFn: () => fetchInbox({ limit: 20 }),
    enabled: open,
  });

  const readMutation = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismiss(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const readAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    void inboxQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groups = groupByAge(inboxQuery.data?.notifications ?? []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Notifications"
        className="relative"
        onClick={() => setOpen(true)}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 ? (
          <Badge
            variant="danger"
            className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full px-1 text-[10px] font-semibold leading-none"
          >
            {unread > 99 ? '99+' : unread}
          </Badge>
        ) : null}
      </Button>
      <SheetContent className="flex flex-col gap-4 p-0">
        <SheetHeader className="border-b border-border px-6 pb-3 pt-6">
          <div className="flex items-center justify-between">
            <SheetTitle>Notifications</SheetTitle>
            <div className="flex items-center gap-2">
              {unread > 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => readAllMutation.mutate()}
                  disabled={readAllMutation.isPending}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </Button>
              ) : null}
            </div>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {inboxQuery.isLoading ? (
            <EmptyState text="Loading…" />
          ) : groups.flat.length === 0 ? (
            <EmptyState
              icon={<BellOff className="h-6 w-6 text-fg-muted" />}
              text="You're all caught up."
            />
          ) : (
            <>
              <Section title="Today" items={groups.today} onRead={readMutation.mutate} onDismiss={dismissMutation.mutate} onNavigate={() => setOpen(false)} />
              <Section title="This week" items={groups.thisWeek} onRead={readMutation.mutate} onDismiss={dismissMutation.mutate} onNavigate={() => setOpen(false)} />
              <Section title="Earlier" items={groups.earlier} onRead={readMutation.mutate} onDismiss={dismissMutation.mutate} onNavigate={() => setOpen(false)} />
            </>
          )}
        </div>
        <div className="border-t border-border px-6 py-3 text-right">
          <Link
            to="/notifications"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => setOpen(false)}
          >
            See all
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EmptyState({ icon, text }: { icon?: React.ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-sm text-fg-muted">
      {icon}
      <div>{text}</div>
    </div>
  );
}

interface SectionProps {
  title: string;
  items: InboxNotification[];
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onNavigate: () => void;
}

function Section({ title, items, onRead, onDismiss: _onDismiss, onNavigate }: SectionProps) {
  if (items.length === 0) return null;
  return (
    <div className="px-4 pt-4">
      <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {title}
      </div>
      <ul className="space-y-1">
        {items.map((n) => (
          <li
            key={n.id}
            className={cn(
              'group flex flex-col gap-1 rounded-md border border-transparent px-3 py-2 transition-colors hover:bg-surface-muted/60',
              !n.readAt && 'bg-primary-subtle/40',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {n.urgency === 'urgent' ? (
                    <Badge variant="danger" className="px-1.5 py-0 text-[10px]">
                      Urgent
                    </Badge>
                  ) : null}
                  <span>{n.title}</span>
                </div>
                <div className="mt-0.5 text-xs text-fg-secondary">{n.body}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-fg-muted">
                  <span>{CATEGORY_LABELS[n.category] ?? n.category}</span>
                  <span aria-hidden>•</span>
                  <span>{relativeTime(n.createdAt)}</span>
                  {n.actionUrl ? (
                    <>
                      <span aria-hidden>•</span>
                      <Link
                        to={toRelativePath(n.actionUrl)}
                        onClick={() => {
                          onRead(n.id);
                          onNavigate();
                        }}
                        className="font-medium text-primary hover:underline"
                      >
                        View
                      </Link>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                {!n.readAt ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRead(n.id)}
                    aria-label="Mark read"
                    className="h-7 w-7"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function groupByAge(items: InboxNotification[]) {
  const today: InboxNotification[] = [];
  const thisWeek: InboxNotification[] = [];
  const earlier: InboxNotification[] = [];
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  for (const n of items) {
    const age = now - new Date(n.createdAt).getTime();
    if (age < dayMs) today.push(n);
    else if (age < 7 * dayMs) thisWeek.push(n);
    else earlier.push(n);
  }
  return { today, thisWeek, earlier, flat: items };
}

function toRelativePath(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin === window.location.origin) return `${u.pathname}${u.search}${u.hash}`;
    return url;
  } catch {
    return url;
  }
}
